import {
  FORCE_INTERACTIVE_LOGIN_KEY,
  SESSION_ENDED_KEY,
} from "./session.constants";
import { captureReturnUrl } from "./sessionReturnUrl";

export function isSessionEnded() {
  try {
    return localStorage.getItem(SESSION_ENDED_KEY) === "true";
  } catch {
    return false;
  }
}

export function setSessionEnded() {
  try {
    localStorage.setItem(SESSION_ENDED_KEY, "true");
  } catch {
    /* ignore */
  }
}

export function clearSessionEnded() {
  try {
    localStorage.removeItem(SESSION_ENDED_KEY);
    localStorage.removeItem(FORCE_INTERACTIVE_LOGIN_KEY);
  } catch {
    /* ignore */
  }
}

export function isForceInteractiveLogin() {
  try {
    return localStorage.getItem(FORCE_INTERACTIVE_LOGIN_KEY) === "true";
  } catch {
    return false;
  }
}

export function setForceInteractiveLogin() {
  try {
    localStorage.setItem(FORCE_INTERACTIVE_LOGIN_KEY, "true");
  } catch {
    /* ignore */
  }
}

export function isOnGoodbyePage() {
  const path = window.location.pathname || "";
  return path === "/goodbye" || path.endsWith("/goodbye");
}

/**
 * Microsoft is returning from an interactive sign-in. MSAL must read this
 * response before the ended-session guard sends the browser to /goodbye.
 */
export function isMsalRedirectReturn() {
  const raw = `${window.location.search || ""}&${window.location.hash || ""}`;
  return /(?:^|[?&#])(code|client_info|error|id_token)=/.test(raw);
}

export function clearClientAuthStorage() {
  try {
    localStorage.removeItem("user");
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.clear();
  } catch {
    /* ignore */
  }
}

/**
 * Sign-out runs in the already open study page. That page must not save
 * itself again while React redirects to Goodbye. A later full page load
 * starts over, so a pasted study link can still be saved.
 */
let allowReturnCapture = true;

export function suppressReturnUrlCapture() {
  allowReturnCapture = false;
}

/**
 * Back and Refresh must not become the post-login destination. A missing
 * navigation entry is treated as a new visit so a pasted study link is kept.
 */
function isHistoryOrReloadNavigation() {
  try {
    const nav = performance.getEntriesByType("navigation")[0];
    const type = nav && nav.type;
    return type === "back_forward" || type === "reload";
  } catch {
    return false;
  }
}

/**
 * After forced logout, Back, Refresh, and a typed study URL must not boot Predict.
 * A new visit to a study URL is remembered so Sign in can open that link.
 * A Microsoft redirect return is left alone so Sign in can finish.
 */
export function enforceSessionEndedGuard({
  fromPersistedPage = false,
  rememberLocation = true,
} = {}) {
  if (!isSessionEnded() || isOnGoodbyePage() || isMsalRedirectReturn()) {
    return false;
  }

  if (
    allowReturnCapture &&
    rememberLocation &&
    !fromPersistedPage &&
    !isHistoryOrReloadNavigation()
  ) {
    captureReturnUrl();
  }

  clearClientAuthStorage();
  window.location.replace(`${window.location.origin}/goodbye`);
  return true;
}

let pageshowGuardInstalled = false;

export function initSessionEndedPageshowGuard() {
  if (pageshowGuardInstalled || typeof window === "undefined") {
    return;
  }
  pageshowGuardInstalled = true;
  window.addEventListener("pageshow", (event) => {
    enforceSessionEndedGuard({ fromPersistedPage: Boolean(event.persisted) });
  });
}
